/**
 * B108 — is this PAR-Q answer bag a COMPLETE, CANONICAL screening?
 *
 * One predicate, no React, no storage, no side effects, so every path that
 * decides "the screening is done" decides it the same way and can be tested on
 * its own (same posture as `lib/onboardingLite.ts` and `lib/onboardingCtaCopy.ts`).
 *
 * ── WHY IT EXISTS ─────────────────────────────────────────────────────────
 * `_parq_completed: true` is a CLINICAL claim: the server's
 * `resolveParqConservative` reads `_parq_flags` and, seeing none, authors at
 * full quality for an athlete who may never have been screened — and nothing
 * re-asks, because the completion flag says the screening happened. Before
 * B108 the completion payload stamped that pair unconditionally, and derived
 * `_parq_flags` from "the bag has at least one key", so:
 *
 *   · an EMPTY bag stamped "screened" with no flags,
 *   · a legacy camelCase bag (`{chestPain: false, dizziness: false}`) passed
 *     the length test and wrote `_parq_flags: []` OVER real earlier flags,
 *     dropping a flagged athlete out of conservative authoring,
 *   · a 6-of-7 bag counted the same as a 7-of-7 one.
 *
 * ── THE RULE ──────────────────────────────────────────────────────────────
 * Every `PARQ_QUESTIONS` key carries an EXPLICIT boolean. Nothing is inferred,
 * defaulted or coerced: a missing key is a missing clinical answer, `'false'`
 * is a string and not an answer, `null` is not an answer, and a key that is not
 * one of the seven canonical ids is not an answer to any of them. Extra keys
 * are ignored rather than rejected (a bag may carry an older question id; it
 * neither completes nor invalidates the seven).
 *
 * The same seven ids `routes/today.tsx:hasCompletionAnswers` checks — that
 * predicate additionally requires the journey, which is a routing fact rather
 * than a clinical one, so it stays where it is and reads this rule for its
 * PAR-Q half.
 */

import { PARQ_QUESTIONS } from '../fixtures/onboarding';

/**
 * True only when `parq` is an object carrying an explicit boolean for every
 * canonical PAR-Q question id.
 *
 * Takes `unknown` deliberately: the bags reaching this predicate come from
 * localStorage JSON that no type ever validated, so the runtime checks have to
 * be real checks rather than assertions TypeScript has already narrowed away.
 */
export function parqAnswersComplete(parq: unknown): boolean {
  if (!parq || typeof parq !== 'object' || Array.isArray(parq)) return false;
  const bag = parq as Record<string, unknown>;
  return PARQ_QUESTIONS.every(
    (q) => Object.prototype.hasOwnProperty.call(bag, q.key) && typeof bag[q.key] === 'boolean',
  );
}
